import { IconChevronDown } from "@tabler/icons-react";
import { type ReactNode, useEffect, useState } from "react";
import TableOfContents from "@/components/mdx/toc";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

export type ArticleTocItem = {
	title: string;
	slug: string;
	items?: ArticleTocItem[];
};

/** Below sticky marketing header (h-14 / sm:h-16) plus a small gap. */
export const ARTICLE_TOC_STICKY_TOP =
	"top-[calc(3.5rem+0.75rem)] sm:top-[calc(4rem+0.75rem)]";

/** Tighter offset on mobile so the foldable ToC sits closer under the header. */
export const ARTICLE_TOC_STICKY_TOP_MOBILE =
	"top-[calc(3.5rem+0.25rem)] sm:top-[calc(4rem+0.75rem)]";

/** Use inside docs/API scroll containers where chrome sits outside the scrollport. */
export const ARTICLE_TOC_STICKY_TOP_SCROLL_CONTAINER = "top-0";

const tocPanelBaseClassName =
	"rounded-xl border border-border/80 bg-card/90 ring-1 ring-primary/15 backdrop-blur-md dark:bg-card/95 dark:ring-primary/20";

const tocInlinePanelClassName =
	"rounded-xl border border-border bg-card shadow-md ring-1 ring-primary/15 dark:ring-primary/20";

const tocSidebarPanelClassName = cn(
	tocPanelBaseClassName,
	"z-20 max-h-[min(70dvh,calc(100dvh-5.5rem))] overflow-y-auto overscroll-contain p-4",
);

type ArticleTocPanelProps = {
	items: ArticleTocItem[];
	/** `inline` — above article body on smaller breakpoints; `sidebar` — right column on xl+. */
	variant: "inline" | "sidebar";
	className?: string;
	/** Override sticky `top` when the ToC scrolls inside a nested container (docs center). */
	stickyTopClassName?: string;
	/** Pin flush to a nested scroll container (docs/API) with full-bleed width and solid stacking. */
	pinToScrollContainer?: boolean;
};

export function ArticleTocPanel({
	items,
	variant,
	className,
	stickyTopClassName,
	pinToScrollContainer = false,
}: ArticleTocPanelProps) {
	// Inline mobile TOC uses Collapsible state — omit on SSR and first hydration paint.
	const [inlineReady, setInlineReady] = useState(variant !== "inline");

	useEffect(() => {
		if (variant === "inline") {
			setInlineReady(true);
		}
	}, [variant]);

	if (!items.length) return null;

	if (variant === "inline" && !inlineReady) {
		return null;
	}

	if (variant === "sidebar") {
		return (
			<aside
				className={cn(
					"sticky hidden min-w-0 self-start xl:block",
					ARTICLE_TOC_STICKY_TOP,
					tocSidebarPanelClassName,
					className,
				)}
			>
				<TableOfContents items={items} />
			</aside>
		);
	}

	return (
		<div
			data-article-toc-inline={pinToScrollContainer ? "" : undefined}
			className={cn(
				"sticky z-40 mb-3 w-full self-start xl:hidden",
				pinToScrollContainer && "-mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8",
				pinToScrollContainer
					? ARTICLE_TOC_STICKY_TOP_SCROLL_CONTAINER
					: (stickyTopClassName ?? ARTICLE_TOC_STICKY_TOP_MOBILE),
				className,
			)}
		>
			<Collapsible
				defaultOpen={false}
				className={cn(
					pinToScrollContainer
						? tocInlinePanelClassName
						: tocPanelBaseClassName,
					"shadow-sm",
				)}
			>
				<CollapsibleTrigger className="group/trigger flex w-full items-center justify-between gap-2 px-4 py-3 text-start outline-none transition-colors hover:bg-muted/30 focus-visible:ring-2 focus-visible:ring-ring/40">
					<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
						On this page
					</span>
					<IconChevronDown
						className="size-4 shrink-0 text-muted-foreground transition-transform duration-200 -rotate-90 group-data-panel-open/trigger:rotate-0"
						aria-hidden
					/>
				</CollapsibleTrigger>
				<CollapsibleContent className="max-h-[min(45dvh,calc(100dvh-7rem))] overflow-y-auto overscroll-contain border-t border-border/60 px-4 pb-4 pt-3">
					<TableOfContents items={items} showHeading={false} />
				</CollapsibleContent>
			</Collapsible>
		</div>
	);
}

/** Two-column article body + optional sticky ToC sidebar. */
export function ArticlePageGrid({
	children,
	sidebar,
	className,
}: {
	children: ReactNode;
	sidebar?: ReactNode;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_12.5rem] xl:gap-12",
				className,
			)}
		>
			<div className="min-w-0">{children}</div>
			{sidebar}
		</div>
	);
}
